// RegisterPage.jsx (compatible avec ton Modal.jsx qui exporte Modal + ModalHeader/Body/Footer en exports nommés)

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import backgroundImage from "./HeaderSlider_02.jpg";
import useScrollToTop from "./../../hooks/useScrollToTop";
import { registerUser, checkEmailExists } from "../../services/userServices";
import { sendNotificationToAdmin } from "../../services/notificationServices";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../components/Modal/Modal";
import Button from "../../components/Button/Button";
import { FaEnvelope, FaUser, FaLock } from "react-icons/fa";

function RegisterPage() {
  useScrollToTop();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  const navigate = useNavigate();

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleResetPassword = () => {
    closeModal();
    navigate("/forgot-password");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setChecking(true);

    try {
      const emailCheck = await checkEmailExists(email);

      if (emailCheck?.error) {
        setErrorMessage(emailCheck.error);
        return;
      }

      if (emailCheck?.exists) {
        openModal();
        return;
      }

      const result = await registerUser(nom, prenom, email, password);

      if (result?.error) {
        setErrorMessage(result.error);
        return;
      }

      // Envoyer une notification à l'administrateur
      const token = localStorage.getItem("token");
      const message = `Un nouvel utilisateur a été créé : ${nom} ${prenom} (${email})`;
      await sendNotificationToAdmin(message, token);

      navigate("/login");
    } catch (err) {
      console.error(err);
      setErrorMessage("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="w-full max-w-md">
        <div className="rounded-3xl bg-white/90 backdrop-blur-sm shadow-2xl ring-1 ring-black/5 p-6 sm:p-8">
          <h1 className="text-center text-3xl font-extrabold tracking-tight text-slate-900">
            Inscription
          </h1>
          <p className="text-center text-sm text-slate-600 mt-2">
            Créez votre compte en quelques secondes.
          </p>

          {errorMessage && (
            <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800 text-sm">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {/* Nom */}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">
                Nom
              </label>
              <div className="mt-1 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-sky-400">
                <FaUser className="text-slate-400" />
                <input
                  type="text"
                  placeholder="Votre nom"
                  className="w-full bg-transparent outline-none text-slate-900 placeholder:text-slate-400"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Prénom */}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">
                Prénom
              </label>
              <div className="mt-1 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-sky-400">
                <FaUser className="text-slate-400" />
                <input
                  type="text"
                  placeholder="Votre prénom"
                  className="w-full bg-transparent outline-none text-slate-900 placeholder:text-slate-400"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">
                Email
              </label>
              <div className="mt-1 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-sky-400">
                <FaEnvelope className="text-slate-400" />
                <input
                  type="email"
                  placeholder="Votre email"
                  className="w-full bg-transparent outline-none text-slate-900 placeholder:text-slate-400"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-autofocus
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">
                Mot de passe
              </label>
              <div className="mt-1 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-sky-400">
                <FaLock className="text-slate-400" />
                <input
                  type="password"
                  placeholder="Choisissez un mot de passe"
                  className="w-full bg-transparent outline-none text-slate-900 placeholder:text-slate-400"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button
              className="w-full h-11 rounded-2xl bg-primary text-white font-semibold shadow-sm transition active:scale-[0.99] hover:opacity-95 disabled:opacity-60"
              type="submit"
              disabled={checking}
            >
              {checking ? "Vérification…" : "S'inscrire"}
            </button>

            <div className="text-center text-sm text-slate-600">
              Déjà un compte ?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="font-semibold text-sky-700 hover:underline"
              >
                Se connecter
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ✅ MODAL compatible avec ton composant */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        size="md"
        labelledById="register-email-exists-title"
        describedById="register-email-exists-desc"
      >
        <ModalHeader
          id="register-email-exists-title"
          title="Email déjà enregistré"
          subtitle="Souhaitez-vous réinitialiser votre mot de passe ?"
          onClose={closeModal}
        />

        <ModalBody id="register-email-exists-desc">
          <p className="text-slate-700 dark:text-slate-200">
            Un compte existe déjà avec l’adresse <b>{email}</b>. Vous pouvez
            réinitialiser votre mot de passe pour récupérer l’accès.
          </p>
        </ModalBody>

        <ModalFooter>
          <Button text="Non" onClick={closeModal} className="btn-ghost" />
          <Button
            text="Oui, réinitialiser"
            onClick={handleResetPassword}
            className="btn-primary"
          />
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default RegisterPage;
